import React, { useEffect, useState } from "react";
import { fmtDate } from "@/lib/dates";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Flame, ArrowRight, Clock, BadgePercent } from "lucide-react";

export function useIntroOffer() {
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    api.get("/billing/bands").then((r) => setOffer(r.data)).catch(() => setOffer(null));
  }, []);
  return offer;
}

function useCountdown(deadline) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!deadline) return null;
  const diff = new Date(deadline).getTime() - now;
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, done: true };
  return { d: Math.floor(diff / 86400000), h: Math.floor(diff / 3600000) % 24, m: Math.floor(diff / 60000) % 60, s: Math.floor(diff / 1000) % 60, done: false };
}

function Segment({ value, label, small }) {
  return (
    <div className="flex flex-col items-center">
      <span className={`font-heading font-bold tabular-nums text-white leading-none ${small ? "text-xl" : "text-2xl md:text-3xl"}`}>{String(value).padStart(2, "0")}</span>
      <span className="text-[10px] uppercase tracking-wider text-white/60 mt-1">{label}</span>
    </div>
  );
}

const fmtDeadline = (iso) => fmtDate(iso);

export function IntroOfferBanner({ variant = "banner" }) {
  const nav = useNavigate();
  const offer = useIntroOffer();
  const cd = useCountdown(offer?.intro_active ? offer.intro_deadline : null);
  if (!offer || !offer.intro_active) return null;
  const deadline = fmtDeadline(offer.intro_deadline);

  if (variant === "compact") {
    return (
      <div data-testid="intro-offer-compact" className="rounded-2xl bg-brand text-white px-5 py-4 mb-6">
        <div className="flex items-center gap-2 text-coral label-caps"><BadgePercent className="h-4 w-4" />Introductory pricing</div>
        <div className="text-sm font-semibold mt-1">{offer.trial_days}-day free trial, then an introductory first-year rate</div>
        <div className="text-xs mt-1 text-white/70">For care homes starting a trial before {deadline}</div>
        {cd && !cd.done && (
          <div className="mt-3 flex items-center gap-3 bg-white/10 rounded-xl px-3 py-1.5 w-fit">
            <Segment small value={cd.d} label="days" /><Segment small value={cd.h} label="hrs" /><Segment small value={cd.m} label="min" /><Segment small value={cd.s} label="sec" />
          </div>
        )}
      </div>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-5 md:px-8">
      <div data-testid="intro-offer-banner" className="rounded-2xl bg-brand text-white p-8 md:p-10">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-xl">
            <span className="label-caps text-coral inline-flex items-center gap-2"><Flame className="h-4 w-4" />Introductory pricing</span>
            <h3 className="text-2xl md:text-3xl font-bold mt-2">{offer.trial_days}-day free trial, then an introductory first-year rate</h3>
            <p className="text-white/70 mt-2">Try KOHC free for {offer.trial_days} days. Care homes starting their trial before {deadline} save up to £{offer.intro_max_saving} on their first-year licence — the introductory price is locked in for the whole trial. Standard renewal price shown upfront, no surprises.</p>
            <div className="mt-3 text-sm inline-flex items-center gap-1.5 text-coral font-semibold"><Clock className="h-4 w-4" />Start your trial by {deadline}</div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-4">
            {cd && !cd.done && (
              <div className="flex items-center gap-4 bg-white/10 rounded-2xl px-5 py-3">
                <Segment value={cd.d} label="days" /><span className="text-white/30 text-2xl font-light -mt-3">:</span>
                <Segment value={cd.h} label="hrs" /><span className="text-white/30 text-2xl font-light -mt-3">:</span>
                <Segment value={cd.m} label="min" /><span className="text-white/30 text-2xl font-light -mt-3">:</span>
                <Segment value={cd.s} label="sec" />
              </div>
            )}
            <Button data-testid="intro-offer-btn" onClick={() => nav("/signup")} className="h-12 px-8 rounded-full bg-coral hover:bg-coral-hover text-white">
              Start free trial <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
